/**
 * The one ordering rule for every dropdown (change 0002, ATA round 3 item 5): options read
 * alphabetically by label, ignoring case and accents, so "émile", "Emile" and "EMILE" sit
 * together and a list is scanned the same way on every screen.
 *
 * Two things keep their place:
 * - a row whose value is the empty string. That is a placeholder or an "All …" / "None"
 *   choice a consumer puts at the head of its list, and it stays at the head, in the order
 *   given;
 * - groups. Grouped options (the searchable picker's Internal Users / Client Contacts) keep
 *   the order their groups first appear in, and are sorted within each group.
 *
 * A list whose order carries meaning (a stage ladder, years, a priority or status
 * progression) opts out at the atom with `preserveOrder` and never reaches this function.
 */
const collator = new Intl.Collator(undefined, { sensitivity: 'base' });

export function compareOptionLabels(a: string, b: string): number {
  return collator.compare(a, b);
}

export function sortOptionsByLabel<T extends { value: string | number; label: string; group?: string }>(
  options: readonly T[],
): T[] {
  const leading: T[] = [];
  const groups = new Map<string | undefined, T[]>();
  for (const option of options) {
    if (option.value === '') { leading.push(option); continue; }
    const bucket = groups.get(option.group);
    if (bucket) bucket.push(option);
    else groups.set(option.group, [option]);
  }
  const sorted: T[] = [...leading];
  for (const bucket of groups.values()) {
    sorted.push(...[...bucket].sort((a, b) => compareOptionLabels(a.label, b.label)));
  }
  return sorted;
}

/** Does `label` start with `letter`, under the same case- and accent-blind rule? */
export function labelStartsWith(label: string, letter: string): boolean {
  const first = label.trim().charAt(0);
  return first !== '' && collator.compare(first, letter) === 0;
}
